import React, { useEffect, useState } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { ClipboardCopy, Share2 } from "lucide-react";
import shareGeneric from "/assets/images/share-generic.png";
import instagramIcon from "/assets/images/insta.png";
import whatsappIcon from "/assets/images/whatsapp.png";
import snapchatIcon from "/assets/images/snap.png";
import facebookIcon from "/assets/images/fb.png";

const WaitingRoom = () => {
  const { challengeId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const [status, setStatus] = useState("waiting");
  const [mode, setMode] = useState("");
  const [inviteCode, setInviteCode] = useState("");
  const [copied, setCopied] = useState(false);
  const [creatorId, setCreatorId] = useState("");
  const [countdown, setCountdown] = useState(null);

  const baseUrl = import.meta.env.VITE_APP_BASE_URL;
  const userId = localStorage.getItem("userId");
  const inviteCodeQuery = searchParams.get("inviteCode");

  const fullInviteUrl = `${window.location.origin}/challenge/waiting/${challengeId}?inviteCode=${inviteCode}`;
  const message = `Join my quiz challenge: ${fullInviteUrl}`;

  // 🟡 Auto-create guest user if not logged in and inviteCode is present
  useEffect(() => {
    if (!userId && inviteCodeQuery) {
      const guestId = `guest${Math.floor(100000 + Math.random() * 900000)}`;
      localStorage.setItem("userId", guestId);
      window.location.reload(); // Trigger reinitialization with guest ID
    }
  }, [userId, inviteCodeQuery]);

  // 🔵 Load challenge details
  useEffect(() => {
    const fetchChallengeDetails = async () => {
      try {
        const res = await axios.get(
          `${baseUrl}/play/challenges/${challengeId}`,
        );
        console.log(res.data);
        setMode(res.data.mode);
        setInviteCode(res.data.inviteCode || "");
        setStatus(res.data.status);
        setCreatorId(res.data.creatorId);

        if (res.data.mode === "sync") {
          const interval = setInterval(async () => {
            try {
              const statusRes = await axios.get(
                `${baseUrl}/play/challenges/${challengeId}/status`,
              );
              setStatus(statusRes.data.status);

              if (statusRes.data.status === "ready") {
                clearInterval(interval);
                // Start countdown when both users are ready
                setCountdown(5);
                const countdownInterval = setInterval(() => {
                  setCountdown((prev) => {
                    if (prev <= 1) {
                      clearInterval(countdownInterval);
                      navigate(`/practice/quiz?challengeId=${challengeId}`);
                      return 0;
                    }
                    return prev - 1;
                  });
                }, 1000);
              } else if (statusRes.data.status === "started") {
                clearInterval(interval);
                navigate(`/practice/quiz?challengeId=${challengeId}`);
              }
            } catch (error) {
              console.error("Error fetching challenge status:", error);
            }
          }, 3000);

          return () => clearInterval(interval);
        }
      } catch (error) {
        console.error("Error fetching challenge details:", error);
      }
    };

    if (userId) {
      fetchChallengeDetails();
    }
  }, [challengeId, navigate, userId, baseUrl]);

  const handleStartAsyncChallenge = () => {
    navigate(
      `/practice/quiz?challengeId=${challengeId}&inviteCode=${inviteCode}`,
    );
  };

  const handleWebShare = () => {
    if (navigator.share) {
      navigator.share({
        title: "Quiz Challenge Invite",
        text: message,
        url: fullInviteUrl,
      });
    } else {
      alert("Web Share API not supported on this browser.");
    }
  };

  // Function to handle cancel invite for sync mode
  const handleCancelInvite = async () => {
    try {
      await axios.delete(`${baseUrl}/play/challenges/${challengeId}`);
      navigate("/dashboard");
    } catch (error) {
      console.error("Failed to delete challenge:", error);
    }
  };

  const getShareUrl = (platform, url) => {
    const text = encodeURIComponent(`Join my quiz challenge!`);
    const encodedUrl = encodeURIComponent(url);
    switch (platform) {
      case "instagram":
        return `https://www.instagram.com/?text=${text}%20${encodedUrl}`;
      case "whatsapp":
        return `https://wa.me/?text=${text}%20${encodedUrl}`;
      case "snapchat":
        return `https://www.snapchat.com/`;
      case "facebook":
        return `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}&quote=${text}`;
      default:
        return `mailto:?subject=Join my challenge!&body=${text}%20${encodedUrl}`;
    }
  };

  return (
    <div className="flex flex-col items-center justify-center h-screen text-center px-4">
      {mode === "sync" ? (
        <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full flex flex-col items-center">
          <div className="bg-blue-100 rounded-full p-4 mb-4 flex items-center justify-center">
            <svg
              width="40"
              height="40"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <circle
                cx="12"
                cy="12"
                r="10"
                fill="#3B82F6"
                fillOpacity="0.15"
              />
              <path
                d="M12 8V12L14.5 13.5"
                stroke="#3B82F6"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <circle
                cx="12"
                cy="12"
                r="9"
                stroke="#3B82F6"
                strokeWidth="1.5"
              />
            </svg>
          </div>

          {countdown ? (
            <>
              <h2 className="text-4xl font-bold text-blue-600 mb-4">
                Starting in {countdown}
              </h2>
              <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
                <div
                  className="h-full bg-blue-400 transition-all duration-1000"
                  style={{ width: `${(countdown / 3) * 100}%` }}
                ></div>
              </div>
            </>
          ) : status === "waiting" && userId === creatorId ? (
            // Creator's view when waiting for opponent
            <>
              <h2 className="text-2xl font-semibold text-gray-800 mb-2">
                Invite Sent!
              </h2>
              <p className="text-gray-600 mb-4">
                We've sent an invite to your friend. Once they join, you'll be
                able to start learning together.
              </p>

              <div className="w-full mb-6">
                <div className="text-sm text-gray-500 mb-2">
                  Waiting for friend to join...
                </div>
                <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-blue-400 transition-all duration-1000 animate-pulse"
                    style={{ width: "40%" }}
                  ></div>
                </div>
              </div>

              <button
                onClick={handleCancelInvite}
                className="w-full flex items-center justify-center gap-2 px-6 py-2 bg-gray-100 text-blue-600 rounded-xl hover:bg-gray-200 transition font-semibold mb-2"
              >
                <span className="text-lg">✖</span> Cancel Invite
              </button>

              <div className="text-xs text-gray-400 mt-2">
                You can also{" "}
                <button
                  className="text-blue-500 underline"
                  onClick={() => window.location.reload()}
                >
                  resend the invite
                </button>{" "}
                or{" "}
                <button
                  className="text-blue-500 underline"
                  onClick={() => navigate("/invite")}
                >
                  invite another friend
                </button>
                .
              </div>
            </>
          ) : (
            // Opponent's view or when challenge is ready
            <>
              <h2 className="text-2xl font-semibold text-gray-800 mb-2">
                Preparing Challenge{" "}
              </h2>
              <p className="text-gray-600 mb-4">
                Get ready! The challenge will start soon.
              </p>

              <div className="w-full mb-6">
                <div className="text-sm text-gray-500 mb-2">
                  Setting up your challenge...
                </div>
                <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-blue-400 transition-all duration-1000 animate-pulse"
                    style={{ width: "40%" }}
                  ></div>
                </div>
              </div>
            </>
          )}
        </div>
      ) : (
        <>
          <h2 className="text-2xl font-semibold text-gray-800 mb-2">
            {mode === "sync"
              ? "Waiting for your friend to join..."
              : "Send this code to your friend"}
          </h2>
          <p className="text-gray-600 mb-1">Challenge ID: {challengeId}</p>
          <p className="text-gray-500 mb-4">Status: {status}</p>

          {mode === "async" && (
            <>
              <div className="bg-gray-100 border border-gray-300 rounded-lg px-4 py-2 mb-4 flex items-center justify-between max-w-full relative">
                <span className="text-sm md:text-lg font-mono text-blue-700 break-all">
                  {fullInviteUrl}
                </span>
                <div className="flex flex-col items-center ml-4">
                  <button
                    onClick={() => {
                      navigator.clipboard.writeText(fullInviteUrl);
                      setCopied(true);
                      setTimeout(() => setCopied(false), 2000);
                    }}
                    className="p-2 rounded hover:bg-purple-100 transition"
                    title="Copy to clipboard"
                  >
                    <ClipboardCopy className="w-5 h-5 text-purple-700" />
                  </button>
                  {copied && (
                    <span className="text-xs text-green-600 mt-1 animate-fade-in">
                      Copied!
                    </span>
                  )}
                </div>
              </div>

              {/* Social Share Row */}
              <div className="flex gap-4 mb-4 flex-wrap justify-center">
                <a
                  href={getShareUrl("generic", fullInviteUrl)}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <img
                    src={shareGeneric}
                    className="w-7 h-7 hover:scale-110 transition-transform"
                  />
                </a>
                <a
                  href={getShareUrl("instagram", fullInviteUrl)}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <img
                    src={instagramIcon}
                    className="w-7 h-7 hover:scale-110 transition-transform"
                  />
                </a>
                <a
                  href={getShareUrl("whatsapp", fullInviteUrl)}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <img
                    src={whatsappIcon}
                    className="w-7 h-7 hover:scale-110 transition-transform"
                  />
                </a>
                <a
                  href={getShareUrl("snapchat", fullInviteUrl)}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <img
                    src={snapchatIcon}
                    className="w-7 h-7 hover:scale-110 transition-transform"
                  />
                </a>
                <a
                  href={getShareUrl("facebook", fullInviteUrl)}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <img
                    src={facebookIcon}
                    className="w-7 h-7 hover:scale-110 transition-transform"
                  />
                </a>
              </div>

              <button
                onClick={handleStartAsyncChallenge}
                className="px-6 py-2 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition mt-6"
              >
                Start Challenge
              </button>
            </>
          )}
        </>
      )}
    </div>
  );
};

export default WaitingRoom;
